<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getUserDetailAction } from '@/api/functions/users/get-user-detail.action'
import { getUserTransactionStatsAction } from '@/api/functions/users/get-user-transaction-stats.action'
import { getUserTransactionsAction } from '@/api/functions/users/get-user-transactions.action'
import { useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import TransactionCard from '@/views/transactions/partials/TransactionCard.vue'

type StatItem = {
  slug: string
  label: string
  value: number
}

const router = useRouter()
const route = useRoute()
const userId = route.params.id as string
const currentPage = ref(1)
const activeFilters = ref<Record<string, unknown>>({})

const tabs = [
  { title: 'اطلاعات اولیه', link: `/users/${userId}` },
  { title: 'تخفیف‌ها', link: `/users/${userId}/discounts` },
  { title: 'شماره حساب‌ها', link: `/users/${userId}/accounts` },
  { title: 'تراکنش‌ها', link: `/users/${userId}/transactions` },
  { title: 'کاربران دعوت شده', link: `/users/${userId}/invited-users` },
]

const { transactionsStatusOptions, discountOptions, currencies, countries } = useGeneralSettings()

const {
  data: userDetail,
  // isLoading: isUserDetailLoading,
  // isError: isUserDetailError,
  // refetch: refetchDetail
} = useQueryApi({
  queryKey: ['user-detail', userId],
  queryFn: () => getUserDetailAction(userId),
  enabled: !!userId,
})

const {
  data: statistics,
  // isLoading: isStatisticsLoading,
  // isError: isStatisticsError,
  // refetch: refetchStatistics
} = useQueryApi({
  queryKey: ['user-transaction-stats', userId],
  queryFn: () => getUserTransactionStatsAction(userId),
  enabled: !!userId,
})

const {
  data: userTransactions,
  isLoading: isUserTransactionsLoading,
  // isError: isUserTransactionsError,
  refetch: refetchUserTransactions,
} = useQueryApi({
  queryKey: computed(() => ['user-transactions', userId, currentPage.value, activeFilters.value]),
  queryFn: () => getUserTransactionsAction(userId, { page: currentPage.value, ...activeFilters.value }),
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

const userData = computed(() => ({
  detail: userDetail.value?.data,
}))

//  Map API data by slug for easier access
const stats = computed<Record<string, StatItem>>(() => {
  const map: Record<string, StatItem> = {}

  if (statistics.value?.data?.length) {
    for (const item of statistics.value.data) {
      map[item.slug] = item
    }
  }

  return map
})

const transactions = computed(() => userTransactions.value?.data ?? [])
const pagination = computed(() => userTransactions.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)

const requisitionsStatus = computed(() => [
  {
    label: stats.value?.total_user_in_progress_transactions?.label ?? 'تعداد تراکنش‌های در حال انجام',
    price: stats.value?.total_user_in_progress_transactions?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: stats.value?.total_user_cancelled_transactions?.label ?? 'تعداد تراکنش‌های لغو شده',
    price: stats.value?.total_user_cancelled_transactions?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: stats.value?.total_user_rejected_transactions?.label ?? 'تعداد تراکنش‌های پذیرفته نشده',
    price: stats.value?.total_user_rejected_transactions?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: stats.value?.total_user_finished_transactions?.label ?? 'تعداد تراکنش‌های موفق',
    price: stats.value?.total_user_finished_transactions?.value ?? 0,
    currency: 'COUNT',
    linkLabel: 'لینک',
    linkTo: `/users/${userId}/transactions?status=FINISHED&page=1`,
  },
  {
    label: stats.value?.total_user_succeed_transactions?.label ?? 'تعداد تراکنش‌های موفق',
    price: stats.value?.total_user_succeed_transactions?.value ?? 0,
    currency: 'COUNT',
  },
])

//  Filter Fields
const filterFields = computed(() => [
  {
    type: 'input',
    key: 'code',
    label: ' کد تراکنش یا کد کاربری را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'select',
    key: 'currency',
    label: 'انتخاب نوع ارز',
    title: 'نوع ارز',
    options: currencies.value,
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت تراکنش',
    title: 'وضعیت تراکنش',
    options: transactionsStatusOptions.value,
  },
  {
    type: 'date',
    key: 'requestDate',
    title: 'تاریخ آخرین تغییرات',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
  {
    type: 'select',
    key: 'origin_bank_account_country',
    label: 'کشور مبدا حساب ارزی',
    title: 'کشور مبدا حساب ارزی',
    options: countries.value,
  },
  {
    type: 'select',
    key: 'destination_bank_account_country',
    label: 'کشور مقصد حساب ارزی',
    title: 'کشور مقصد حساب ارزی',
    options: countries.value,
  },
  {
    type: 'select',
    key: 'discount',
    label: 'انتخاب تخفیف',
    title: 'تخفیف',
    options: discountOptions.value,
  },
])

const userStatusText = computed(() => {
  const status = userData?.value?.detail?.status

  switch (status) {
    case 'VERIFIED':
      return 'تایید شده'
    case 'EMAIL_VERIFICATION_PENDING':
      return 'ثبت نام اولیه'
    case 'MOBILE_REGISTRATION_PENDING':
      return 'در انتظار ثبت شماره موبایل'
    case 'IDENTIFICATION_REGISTRATION_PENDING':
      return 'در انتظار ارسال مدارک هویتی'
    case 'IDENTIFICATION_VERIFICATION_PENDING':
      return 'در انتظار تایید مدارک هویتی'
    case 'MOBILE_VERIFICATION_PENDING':
      return 'در انتظار تایید شماره موبایل'
    default:
      return ''
  }
})

const onApplyFilters = (values: Record<string, unknown>) => {
  activeFilters.value = { ...values }
  currentPage.value = 1

  const query = Object.fromEntries(
    Object.entries(values).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ) as Record<string, string | string[] | null>

  router.push({
    path: route.path,
    query,
  })
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1
  router.push({ path: route.path, query: {} })
}

const goToPage = (page: number) => {
  currentPage.value = page
  refetchUserTransactions()
}
</script>

<template>
    <div class="mb-9">

        <router-link to="/users"
            class="w-fit mb-4 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
            <img src="/svg/back-icon.svg" class="me-1" width="22" />
            <span>بازگشت</span>
        </router-link>

        <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-6">
            <div class="w-full lg:w-auto flex flex-row justify-between items-center gap-8 ">
                <div class="text-[#1b325a] text-[22px]">
                    کاربر شماره <span class="font-fa">{{ userData?.detail?.code }}</span>
                </div>
                <div class="w-auto flex flex-row items-center justify-between lg:justify-start gap-2">
                    <!-- status  -->
                    <div
                        class="w-fit flex items-center bg-[#d5ddea] text-[#2d5396] justify-center text-[13px] font-medium h-10 rounded-[10px] px-5 me-1">
                        {{ userStatusText }}
                    </div>
                </div>
            </div>
        </div>

        <Tabs :tabs="tabs" :isAdminPanel="true" class="mt-8" />

        <div class="w-full mb-12 mt-10 pt-1">

            <div class="w-full grid grid-cols-1 md:grid-cols-3 gap-6 gap-y-9">
                <template v-for="(stat, index) in requisitionsStatus" :key="`stat-item-${index}`">
                    <StatusCard :data="stat" />
                </template>
            </div>

            <h2 class="text-[22px] text-[#1b325a ]  mt-11">لیست تراکنش‌ها</h2>
            <!--  Filters Section -->
            <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-6" />

            <!-- 👉 Main Cards && Pagination  -->
            <div v-if="isUserTransactionsLoading" class="text-center py-8 text-gray-500">
                در حال دریافت اطلاعات تراکنش‌ها...
            </div>
            <div v-else>
                <div v-if="transactions.length > 0" class="mt-8 flex flex-col gap-6">
                    <!-- TransactionCard loop -->
                    <TransactionCard v-for="(transaction, index) in transactions" :key="`transaction-${index}`"
                        :transaction="transaction" />

                    <Pagination v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage"
                        :totalItems="totalItems" @update:currentPage="goToPage" :isAdmin="true" />
                </div>
                <EmptyState v-else :onReset="onResetFilters" />
            </div>
        </div>

    </div>
</template>
